import {
  currencyFormatter,
  currencyFormatterWithoutDecimals,
  percentFormatter,
  percentFormatterWithoutDecimals,
  percentFormatterWithTwoDP,
} from "shared-utils";

import type { MetricType } from "../utils/types";

interface FormatValueParams {
  value: string;
  metricType?: MetricType;
  useDash?: boolean;
  rateToFixed?: number;
  withDecimal?: boolean;
  hackPercentage?: boolean;
  narrowCurrency?: boolean;
  twoDecimalPlacesForPercentages?: boolean;
}

export const formatValue = (formatValueParams: FormatValueParams): string => {
  const {
    value,
    metricType,
    useDash = true,
    rateToFixed = 1,
    withDecimal,
    hackPercentage = false,
    narrowCurrency = false,
    twoDecimalPlacesForPercentages = false,
  } = formatValueParams;

  if (metricType?.type === "STRING") {
    return value;
  }

  if (value == null || isNaN(parseFloat(value)) || !metricType) {
    return useDash ? "-" : "";
  }

  const withoutDecimals: boolean =
    withDecimal === undefined ? Math.abs(parseFloat(value)) >= 100 : Math.abs(parseFloat(value)) >= 100 || !withDecimal;

  switch (metricType.type) {
    case "DECIMAL":
      if (withoutDecimals) {
        return Math.round(parseFloat(value)).toString();
      }
      return `${parseFloat(value).toFixed(rateToFixed)}`;
    case "COUNT":
      return `${parseInt(value).toLocaleString()}`;
    case "RATE":
      if (withoutDecimals) {
        return `${parseFloat(value).toFixed(0)}x`;
      }
      return `${parseFloat(value).toFixed(rateToFixed)}x`;
    case "PERCENTAGE": {
      const _value = hackPercentage ? 100 * parseFloat(value) : parseFloat(value);
      if (withoutDecimals) {
        return percentFormatterWithoutDecimals.format(_value * 0.01);
      }
      if (twoDecimalPlacesForPercentages) {
        return percentFormatterWithTwoDP.format(_value * 0.01);
      } else {
        return percentFormatter.format(_value * 0.01);
      }
    }
    case "CURRENCY":
      if (withoutDecimals) {
        return currencyFormatterWithoutDecimals(
          metricType.currencyCode,
          narrowCurrency ? "narrowSymbol" : "symbol"
        ).format(parseFloat(value));
      }
      return currencyFormatter(metricType.currencyCode, narrowCurrency ? "narrowSymbol" : "symbol").format(
        parseFloat(value)
      );
    default:
      throw Error(`Can't format metricType '${metricType}'`);
  }
};
